"use client";

import {
  DEFAULT_IMAGE_OPTIMIZER_FORMAT,
  IMAGE_OPTIMIZER_HOSTS,
  IMAGE_OPTIMIZER_VARIANT_PLACEHOLDER,
} from "./constants";

import { imageSourcePath, isOwnedImageUrl, normalizeSourcePath } from "./source";

export {
  ALLOWED_DEVICE_SIZES,
  ALLOWED_IMAGE_SIZES,
  ALLOWED_WIDTHS,
  DEFAULT_IMAGE_OPTIMIZER_FORMAT,
  IMAGE_OPTIMIZER_HOST,
  IMAGE_OPTIMIZER_HOSTS,
  IMAGE_OPTIMIZER_VARIANT_PLACEHOLDER,
} from "./constants";

const LOW_QUALITY = 70;
const MEDIUM_QUALITY = 80;
const HIGH_QUALITY = 85;
const MEDIUM_QUALITY_THRESHOLD = 80;
const HIGH_QUALITY_THRESHOLD = 85;
const OPTIMIZER_PATH_PREFIX = "/images/";
const VARIANT_RE = /^w\d+-q\d+\.(?:jpe?g|webp|avif)$/;

export type OctogenImageLoaderProps = {
  src: string;
  width: number;
  quality?: number;
};

export type ImageOptimizerClientOptions = {
  sourceCdnHost: string;
  routePrefix: string;
};

export type ImageOptimizerSrcOptions = ImageOptimizerClientOptions & {
  isEnabled: boolean;
};

export function selectImageOptimizerQuality(quality?: number): number {
  if (quality !== undefined && quality >= HIGH_QUALITY_THRESHOLD) return HIGH_QUALITY;
  if (quality !== undefined && quality >= MEDIUM_QUALITY_THRESHOLD) return MEDIUM_QUALITY;
  return LOW_QUALITY;
}

export function createImageOptimizerVariant({
  width,
  quality,
  format = DEFAULT_IMAGE_OPTIMIZER_FORMAT,
}: {
  width: number;
  quality?: number;
  format?: "avif" | "webp" | "jpeg";
}): string {
  return `w${width}-q${selectImageOptimizerQuality(quality)}.${format}`;
}

function replaceExistingOptimizerVariant(src: string, variant: string): string | null {
  const hasProtocol = /^[a-z][a-z\d+\-.]*:\/\//i.test(src);
  const url = new URL(src, hasProtocol ? undefined : "https://placeholder.octogen.local");

  if (!isOwnedImageUrl(url, IMAGE_OPTIMIZER_HOSTS) && hasProtocol) {
    return null;
  }
  if (!url.pathname.startsWith(OPTIMIZER_PATH_PREFIX)) {
    return null;
  }

  const pathParts = url.pathname.split("/");
  const variantIndex = 3;
  if (!VARIANT_RE.test(pathParts[variantIndex] ?? "")) {
    return null;
  }

  pathParts[variantIndex] = variant;
  url.pathname = pathParts.join("/");

  if (hasProtocol) {
    return url.toString();
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

export const normalizeImageOptimizerSourcePath = normalizeSourcePath;

export function getImageOptimizerSourcePathFromSrc(
  src: string,
  sourceCdnHost: string
): string | null {
  return imageSourcePath(src, sourceCdnHost);
}

export function createImageOptimizerSrc(src: string, options: ImageOptimizerSrcOptions): string {
  if (!options.isEnabled) {
    return src;
  }

  const sourcePath = getImageOptimizerSourcePathFromSrc(src, options.sourceCdnHost);
  if (!sourcePath) {
    return src;
  }

  return `${options.routePrefix}${IMAGE_OPTIMIZER_VARIANT_PLACEHOLDER}?source=${encodeURIComponent(
    sourcePath
  )}`;
}

export function isImageOptimizerSrc(
  src: string,
  { routePrefix }: ImageOptimizerClientOptions
): boolean {
  if (!src) {
    return false;
  }

  if (src.includes(IMAGE_OPTIMIZER_VARIANT_PLACEHOLDER)) {
    return true;
  }

  try {
    const url = new URL(src, "https://placeholder.octogen.local");
    return (
      url.pathname.startsWith(routePrefix) ||
      (isOwnedImageUrl(url, IMAGE_OPTIMIZER_HOSTS) &&
        url.pathname.startsWith(OPTIMIZER_PATH_PREFIX))
    );
  } catch {
    return false;
  }
}

export function shouldUseImageOptimizerLoader(
  src: string,
  options: ImageOptimizerSrcOptions
): boolean {
  return options.isEnabled && isImageOptimizerSrc(src, options);
}

export default function octogenImageLoader({
  src,
  width,
  quality,
}: OctogenImageLoaderProps): string {
  const variant = createImageOptimizerVariant({ width, quality });
  if (src.includes(IMAGE_OPTIMIZER_VARIANT_PLACEHOLDER)) {
    return src.split(IMAGE_OPTIMIZER_VARIANT_PLACEHOLDER).join(variant);
  }

  return replaceExistingOptimizerVariant(src, variant) ?? src;
}
